import type { ReactNode } from "react";
import "./DashboardLedger.css";

export type DashboardStateStatus = "loading" | "ready" | "empty" | "error";
export type DashboardStateScope = "global" | "section";

export interface DashboardStateProps {
  status: DashboardStateStatus;
  scope?: DashboardStateScope;
  updating?: boolean;
  title?: string;
  message?: string;
  onRetry?: () => void;
  children?: ReactNode;
}

const emptyCopy = {
  global: { title: "Sem movimento", message: "Sem dados neste período." },
  section: { title: "Sem dados", message: "Nada para mostrar." },
} as const;

const errorCopy = {
  global: {
    title: "Dashboard indisponível",
    message: "Não foi possível carregar o dashboard.",
  },
  section: {
    title: "Seção indisponível",
    message: "Não foi possível carregar esta parte.",
  },
} as const;

export function DashboardState({
  status,
  scope = "global",
  updating = false,
  title,
  message,
  onRetry,
  children,
}: DashboardStateProps) {
  const className = `dashboard-ledger dashboard-ledger-state dashboard-ledger-state--${scope}`;

  if (status === "loading") {
    return (
      <div
        className={className}
        role="status"
        aria-label="Carregando dashboard"
      >
        <div
          className="dashboard-ledger-state__skeleton-metrics"
          aria-hidden="true"
        >
          {Array.from({ length: 4 }, (_, index) => (
            <div
              className="dashboard-ledger-state__skeleton-metric"
              data-skeleton="metric"
              key={index}
            >
              <span className="dashboard-ledger-state__skeleton-line" />
              <span className="dashboard-ledger-state__skeleton-line" />
            </div>
          ))}
        </div>
        <div
          className="dashboard-ledger-state__skeleton-body"
          data-skeleton="body"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (status === "ready") {
    return (
      <div
        className={className}
        data-testid="dashboard-state"
        aria-busy={updating ? "true" : "false"}
      >
        {updating ? (
          <>
            <div
              className="dashboard-ledger-state__updating-line"
              aria-hidden="true"
            />
            <span className="dashboard-ledger-state__sr-status" role="status">
              Atualizando
            </span>
          </>
        ) : null}
        {children}
      </div>
    );
  }

  const copy = status === "empty" ? emptyCopy[scope] : errorCopy[scope];
  const role = status === "error" ? "alert" : "status";
  return (
    <div className={className} role={role}>
      <div className="dashboard-ledger-state__message">
        <div className="dashboard-ledger-state__copy">
          <h2>{title ?? copy.title}</h2>
          <p>{message ?? copy.message}</p>
          {status === "error" && onRetry ? (
            <button
              type="button"
              className="dashboard-ledger-state__retry"
              onClick={onRetry}
            >
              Tentar novamente
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
